import { useEffect, useRef, useState } from "react";
import { Bookmark, Trash2 } from "lucide-react";

import { Modal } from "@components/items/display/Modal";
import { ProgressBar } from "@components/items/display/ShadcnProgressBar";
import { Avatar } from "@ui/avatar";
import { Card, CardContent, CardTitle } from "@ui/card";
import {
  clearBookmarkedDetails,
  clearHighlightedDetails,
  clearSelectedPlayerContent,
  getBookmarkedDetails,
  getHighlightedDetails,
  removeBookmark,
  removeHighlight,
  setAlert,
  useGetBookmarkedDetails,
  useGetHighlightedDetails,
} from "@lib/stores";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { GenericLoading } from "@components/items/common/GenericLoading";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import {
  useRemoveBookmark,
  useRemoveHighlight,
} from "@lib/stores/content/hooks";
import TranslatedText from "@components/items/display/TranslatedText";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";

export type LibraryItemDetails = {
  id: string | undefined;
  contentEntityTypeId: number | undefined;
  title: string | undefined;
  author: string | undefined;
  image: string | undefined;
  progress: number | undefined;
  count: number | undefined;
  contentEntityType: string | undefined;
  isLocked: boolean | undefined;
};

interface LibraryItemDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onItemDelete: () => void;
  type: "Bookmarked" | "Highlighted" | undefined;
  details: LibraryItemDetails;
}

export const LibraryItemDetailsModal = ({
  isOpen,
  onClose,
  onItemDelete,
  type,
  details,
}: LibraryItemDetailsModalProps) => {
  const navigate = useAppNavigate();

  const [isItemRemoveModalOpen, setIsItemRemoveModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | string>();

  const { data: bookmarkedDetails, status: bookmarkedDetailsStatus } =
    useGetBookmarkedDetails();
  const { data: highlightedDetails, status: highlightedDetailsStatus } =
    useGetHighlightedDetails();
  const { status: removeHighlightStatus } = useRemoveHighlight();
  const { status: removeBookmarkStatus } = useRemoveBookmark();

  const { onForcedPlayingChapterSelect } = useSelectedPlayerContent();

  const detailsData =
    type === "Bookmarked"
      ? bookmarkedDetails.map((b) => {
          return {
            id: b.id,
            text: b.chapterTitle,
            chapterId: b.chapterId,
          };
        })
      : highlightedDetails.map((h) => {
          return {
            id: h.id,
            text: h.text,
            chapterId: h.contentEntityId,
          };
        });

  const onItemRemoveModalClose = () => {
    setIsItemRemoveModalOpen(false);
    setSelectedItemId(undefined);
  };

  useEffect(() => {
    if (details.id) {
      if (type === "Bookmarked") {
        getBookmarkedDetails(details.id);
      } else {
        getHighlightedDetails(details.id);
      }
    }

    return () => {
      clearBookmarkedDetails();
      clearHighlightedDetails();
    };
  }, [details.id]);

  const color = type === "Bookmarked" ? "#FD7777" : "#F6E093";

  return (
    <Modal isOpen={isOpen} onClose={onClose} zIndexClassName="z-[60]">
      <div className="w-full bg-white text-black dark:bg-dark_background dark:text-white">
        <div className="flex w-full items-start justify-start gap-8 bg-[#F3F3F3] dark:bg-[#2D2E35]">
          <SavedItemCard
            count={
              type === "Bookmarked"
                ? bookmarkedDetails.length
                : highlightedDetails.length
            }
            image={details.image || ""}
            title={details.title || ""}
            author={details.author || ""}
            progress={details.progress || 0}
            color={color}
            onClick={() => {
              navigate(`/details/${details.id}/`);
            }}
          />
          {bookmarkedDetailsStatus === "loading" ||
          highlightedDetailsStatus === "loading" ? (
            <GenericLoading />
          ) : (
            <div className="w-full max-w-[700px] space-y-4 pt-5">
              {detailsData.map(({ text, chapterId, id }) => (
                <SavedTextCard
                  key={`${chapterId}-${id}`}
                  text={text || ""}
                  color={color}
                  type={type}
                  onClick={() => {
                    if (details.isLocked) {
                      setAlert({
                        show: true,
                        for: "page",
                        type: AlertType.WARNING,
                        messages: [
                          {
                            code: 0,
                            field: null,
                            message: translate(
                              "content-details.messages.subscribe to listen"
                            ),
                          },
                        ],
                      });

                      return;
                    }

                    clearSelectedPlayerContent();

                    onForcedPlayingChapterSelect(chapterId);

                    navigate(`/details/${details.id}/`);
                  }}
                  onDelete={() => {
                    setSelectedItemId(type === "Highlighted" ? id : chapterId);
                    setIsItemRemoveModalOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
        {isItemRemoveModalOpen && (
          <ItemRemoveModal
            isOpen={isItemRemoveModalOpen}
            onClose={onItemRemoveModalClose}
            onSubmit={() => {
              if (selectedItemId) {
                if (type === "Highlighted") {
                  removeHighlight(
                    selectedItemId as number,
                    () => {},
                    () => {
                      details.id && getHighlightedDetails(details.id);

                      onItemDelete();

                      onItemRemoveModalClose();
                    }
                  );
                } else {
                  removeBookmark(
                    { contentEntityId: selectedItemId as string },
                    () => {},
                    () => {
                      details.id && getBookmarkedDetails(details.id);

                      onItemDelete();

                      onItemRemoveModalClose();
                    }
                  );
                }
              }
            }}
            isLoading={
              removeHighlightStatus === "loading" ||
              removeBookmarkStatus === "loading"
            }
            type={type}
          />
        )}
      </div>
    </Modal>
  );
};

interface SavedItemCardProps {
  count: number;
  image: string;
  title: string;
  author: string;
  progress: number;
  color: string;
  onClick?: () => void;
}

const SavedItemCard = ({
  count,
  image,
  title,
  author,
  progress,
  color,
  onClick,
}: SavedItemCardProps) => {
  return (
    <Card
      className="flex w-[240px] cursor-pointer flex-wrap border-none p-5 dark:bg-[#3C3B40]"
      onClick={onClick}
    >
      <CardContent className="space-y-1 p-0">
        <img
          src={image}
          alt={title}
          className="h-[200px] w-[210px] rounded-md"
        />
        <div className="flex items-center justify-center gap-3">
          <ProgressBar value={progress} height="h-1" />
          <div className="text-wajeez">{progress}%</div>
        </div>
      </CardContent>
      <CardTitle className="mt-3 flex items-center justify-start gap-3 py-3">
        <div className="w-[150px] space-y-1.5 text-wrap">
          <div className="text-[20px] font-medium ltr:text-left rtl:text-right dark:text-white">
            {title}
          </div>
          <div className="text-[16px] font-normal text-[#898A8D] ltr:text-left rtl:text-right">
            {author}
          </div>
        </div>
        <Avatar
          className="flex items-center justify-center p-3 text-[20px] text-white"
          style={{ backgroundColor: color }}
        >
          {count}
        </Avatar>
      </CardTitle>
    </Card>
  );
};

interface SavedTextCardProps {
  text: string;
  color: string;
  onDelete: () => void;
  onClick: () => void;
  type: "Bookmarked" | "Highlighted" | undefined;
}

const SavedTextCard = ({
  text,
  onClick,
  color,
  onDelete,
  type,
}: SavedTextCardProps) => {
  const deleteButtonRef = useRef<HTMLDivElement>(null);

  return (
    <Card
      className="relative flex w-full cursor-pointer items-center justify-between rounded-xl border-y-0 border-e-0 border-s-8 px-6 py-5 text-[16px] font-medium dark:bg-[#3C3B40]"
      style={{
        borderInlineStartColor: color,
      }}
      onClick={(event) => {
        if (
          deleteButtonRef.current &&
          deleteButtonRef.current.contains(event.target as HTMLElement)
        ) {
          return;
        }

        onClick();
      }}
    >
      <div>{text}</div>
      <div ref={deleteButtonRef} className="px-5" onClick={onDelete}>
        {type === "Bookmarked" ? (
          <Bookmark
            size={28}
            style={{
              fill: color,
              color,
            }}
          />
        ) : (
          <Trash2 size={28} stroke={color} />
        )}
      </div>
    </Card>
  );
};

export default SavedTextCard;

interface ItemRemoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  type: "Bookmarked" | "Highlighted" | undefined;
}

const ItemRemoveModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  type,
}: ItemRemoveModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      zIndexClassName="z-[70]"
      contentClassName="w-[400px]"
    >
      <div className="relative">
        <TranslatedText
          text={`my-library.${
            type === "Bookmarked"
              ? "remove bookmark text"
              : "remove highlight text"
          }`}
          className="text-center text-black dark:text-white"
        />
        <div className="mt-6 flex items-center justify-center gap-4">
          <ShadcnButton
            className="!flex bg-primary text-white hover:bg-primary/80"
            onClick={onSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          >
            <TranslatedText text={`my-library.${"remove"}`} />
          </ShadcnButton>
          <ShadcnButton
            className="!flex bg-transparent text-black hover:bg-transparent dark:text-white"
            onClick={onClose}
          >
            <TranslatedText
              text={`my-library.${"cancel"}`}
              className="text-[16px] font-normal leading-[22px]"
            />
          </ShadcnButton>
        </div>
      </div>
    </Modal>
  );
};
